// Emily page-signal — fire-and-forget page-view events to Emily's rules/scoring
// pipeline, keyed by the HubSpot `hubspotutk` cookie.
//
// The initial "opened the build page" signal fires from index.html on load. This
// module fires RICHER intent signals from the app — e.g. "configured a machine
// and saw a price" on Calculate — with a distinct URL/title marker so Emily can
// score a serious configurator session far above a bounce.

const PAGE_SIGNAL_URL = "https://emily.gammilldealer.com/webhooks/page-signal/";

function hubspotutk(): string {
  const m = document.cookie.match("(^|;)\\s*hubspotutk\\s*=\\s*([^;]+)");
  return m ? (m.pop() ?? "") : "";
}

/** POST a page-signal to Emily. No-ops without a hubspotutk cookie (HubSpot's
 *  tracking sets it). Fire-and-forget — never awaited, never throws. */
export function sendPageSignal(opts?: { url?: string; title?: string }): void {
  const hutk = hubspotutk();
  if (!hutk) return;
  try {
    fetch(PAGE_SIGNAL_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain" }, // simple request — no preflight
      keepalive: true,
      body: JSON.stringify({
        hutk,
        url: opts?.url ?? location.href,
        title: opts?.title ?? document.title,
        ts: Date.now(),
        referrer: document.referrer || "",
      }),
    });
  } catch {
    /* fire-and-forget */
  }
}

/** High-intent signal: the shopper clicked Calculate and saw a price. Marked so
 *  Emily can weight it above the plain page-open signal. */
export function signalPriceCalculated(): void {
  sendPageSignal({
    url: location.href.split("#")[0] + "#calculated",
    title: "Gammill Build-A-Machine — Price Calculated",
  });
}

/** Hottest signal: the shopper generated an actual quote ("Email me the quote").
 *  Top of the intent ladder — they asked for a real quote on their build. */
export function signalQuoteRequested(): void {
  sendPageSignal({
    url: location.href.split("#")[0] + "#quoted",
    title: "Gammill Build-A-Machine — Quote Requested",
  });
}

/** Strong buying signal: the shopper submitted a financing prequalification
 *  request — they're planning how to pay for it. */
export function signalPrequalifyRequested(): void {
  sendPageSignal({
    url: location.href.split("#")[0] + "#prequalify",
    title: "Gammill Build-A-Machine — Prequalify Requested",
  });
}
